import {differenceInDays, subDays} from "date-fns";
import {useEffect} from "react";
import {toast} from "react-hot-toast";

import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {DatePicker} from "@/components/datePicker/DatePicker.tsx";
import {DateRangeOption} from "@/components/datePicker/subComponents/DatePickerShortcutList/DatePickerShortcutList.utils.ts";
import {RequiredDateRange} from "@/components/datetime/utils.ts";
import {StandardDateRange} from "@/lib/queries/reports.ts";

interface RangePickerProps {
  range: RequiredDateRange;
  fixedRange?: StandardDateRange;
  onChange: (range: RequiredDateRange | null, fixedRange?: StandardDateRange | null) => void;
  className?: string;
  maxRange?: number;
  size?: "small" | "large";
  defaultRange?: DateRangeOption;
}

export const UserDateRangePicker = ({
  range,
  fixedRange,
  className,
  maxRange = 90,
  onChange,
  size,
}: RangePickerProps) => {
  const {user} = useCurrentUserContext();

  useEffect(() => {
    if (maxRange && range?.to && differenceInDays(range.to, range.from) > maxRange) {
      range.from = subDays(range.to, maxRange);
      toast.error(`Range is limited to ${maxRange} days. The start date has been adjusted.`);
      onChange(range, null);
    }
  }, [onChange, range, maxRange]);

  return (
    <>
      <DatePicker
        mode={"range"}
        inputProps={{size: size}}
        hasPopoverInput
        fixedRange={fixedRange}
        className={className}
        selected={range}
        onChange={(selected, fixedRange) => {
          const dateRange = !selected?.to || !selected?.from ? null : (selected as RequiredDateRange);
          onChange(dateRange, fixedRange);
        }}
        disableFutureDates
        timezone={user?.account.timezone}
        dateFormat={user!.current_date_format}
      />
    </>
  );
};
